import { useEffect, useState } from "react";
import { Plus, X, Send, Mail } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DeliverabilityCard } from "../../components/admin/DeliverabilityCard";

const btn = "bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors";

const TestResults = ({ results }) => (
  <ul className="mt-4 space-y-1 text-sm" data-testid="test-email-results">
    {results.map((r) => (
      <li key={r.email} data-testid={`test-email-result-${r.email}`} className={r.sent ? "text-green-700" : "text-red-700"}>
        {r.sent ? "✓ Enviado para" : "✗ Falha no envio para"} {r.email}
      </li>
    ))}
  </ul>
);

export default function SettingsPage() {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => { api.get("/admin/settings/notifications").then((r) => setEmails(r.data.booking_emails)).catch(() => {}); }, []);

  const add = (e) => {
    e.preventDefault();
    const v = newEmail.trim().toLowerCase();
    if (v && !emails.includes(v)) setEmails([...emails, v]);
    setNewEmail("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/notifications", { booking_emails: emails });
      setEmails(data.booking_emails);
      toast.success("Configurações salvas!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const test = async () => {
    setTesting(true); setResults(null);
    try {
      const { data } = await api.post("/admin/settings/notifications/test");
      setResults(data.results);
      toast[data.results.every((r) => r.sent) ? "success" : "error"](data.results.every((r) => r.sent) ? "E-mails de teste enviados" : "Algum envio falhou");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setTesting(false);
  };

  return (
    <div className="max-w-4xl" data-testid="admin-settings-page">
      <h1 className="text-2xl font-serif text-[#9B6179] mb-6">Configurações</h1>
      <div className="bg-white p-6 rounded-[2px] border border-[#9B6179]/5 space-y-4">
        <div className="flex items-center gap-2 text-[#6F3F55]"><Mail className="w-4 h-4" /><p className="font-serif text-lg">E-mails de notificação de agendamentos</p></div>
        <p className="text-sm text-[#8A7980]">Estes endereços recebem os novos pedidos, os cancelamentos e os pedidos de remarcação feitos pelas clientes.</p>
        <ul className="space-y-2">
          {emails.map((e) => (
            <li key={e} className="flex items-center justify-between bg-[#F7F0E8] px-3 py-2 rounded-[2px] text-sm" data-testid={`notification-email-${e}`}>
              {e}
              <button onClick={() => setEmails(emails.filter((x) => x !== e))} data-testid={`remove-notification-email-${e}`}><X className="w-4 h-4 text-[#6F3F55]" /></button>
            </li>
          ))}
        </ul>
        <form onSubmit={add} className="flex gap-2">
          <div className="flex-1"><Label className="sr-only">Novo e-mail</Label><Input type="email" placeholder="novo@email.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="notification-email-input" /></div>
          <Button type="submit" variant="outline" className="rounded-[2px]" data-testid="notification-email-add"><Plus className="w-4 h-4 mr-1" /> Adicionar</Button>
        </form>
        <div className="flex gap-2 pt-2">
          <Button onClick={save} disabled={saving || emails.length === 0} className={btn} data-testid="notification-settings-save">{saving ? "Salvando..." : "Salvar"}</Button>
          <Button onClick={test} disabled={testing} variant="outline" className="rounded-[2px]" data-testid="notification-test-button"><Send className="w-4 h-4 mr-1" /> {testing ? "Enviando..." : "Enviar e-mail de teste"}</Button>
        </div>
        <p className="text-xs text-[#8A7980]">O teste é enviado para os endereços já salvos.</p>
        {results && <TestResults results={results} />}
      </div>
      <DeliverabilityCard key={results ? JSON.stringify(results) : "init"} />
    </div>
  );
}
